import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { FileDownload as ExportIcon } from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import { useTheme } from "@mui/material/styles";

import CronBuilderBox from "../../../components/CronBuilderBox";
import { getMutedPanelSx } from "../../../theme/panelStyles";
import { nexaliaApi } from "../../../services/api/omni/nexalia";

const emptyCredForm = {
  email: "",
  password: "",
  categories: "Beauty-Styling",
  in_stock_qty: 1,
};

const NexaliaTab = () => {
  const theme = useTheme();

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [credentials, setCredentials] = useState([]);
  const [credForm, setCredForm] = useState(emptyCredForm);
  const [editingId, setEditingId] = useState(null);
  const [credLoading, setCredLoading] = useState(false);

  const [cronValue, setCronValue] = useState("0 4 * * *");
  const [cronStatus, setCronStatus] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalItems, setTotalItems] = useState(0);

  const fetchCredentials = async () => {
    try {
      const res = await nexaliaApi.listCredentials();
      setCredentials(res.data || []);
      if (res.data?.length && !editingId) {
        const active = res.data.find((c) => c.active) || res.data[0];
        setCredForm({
          email: active.email || "",
          password: "",
          categories: (active.categories || []).join(", "),
          in_stock_qty: active.in_stock_qty ?? 1,
        });
        setEditingId(active.id);
      }
    } catch (err) {
      console.error("Failed to load Nexalia credentials", err);
    }
  };

  const fetchStatus = async () => {
    try {
      setCronLoading(true);
      const res = await nexaliaApi.getCronStatus();
      setCronStatus(res.data);
      if (res.data?.expression) {
        setCronValue(res.data.expression);
      }
    } catch (err) {
      console.error("Failed to fetch Nexalia cron status", err);
    } finally {
      setCronLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const res = await nexaliaApi.getProducts(
        page + 1,
        pageSize,
        search,
        sortBy,
        sortOrder,
      );
      setProducts(res.data.items || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch Nexalia products", err);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
    fetchStatus();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [page, pageSize, search, sortBy, sortOrder]);

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const parseCategories = (raw) =>
    String(raw || "")
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const saveCredentials = async () => {
    try {
      setCredLoading(true);
      setError(null);
      setSuccess(null);
      const payload = {
        email: credForm.email.trim(),
        categories: parseCategories(credForm.categories),
        in_stock_qty: Number(credForm.in_stock_qty) || 1,
        active: true,
      };
      if (credForm.password) {
        payload.password = credForm.password;
      }
      if (editingId) {
        if (!payload.password) {
          delete payload.password;
        }
        await nexaliaApi.updateCredentials(editingId, payload);
        setSuccess("Credentials updated.");
      } else {
        if (!payload.password) {
          setError("Password is required for new credentials.");
          return;
        }
        const res = await nexaliaApi.createCredentials(payload);
        setEditingId(res.data.id);
        setSuccess("Credentials saved.");
      }
      setCredForm((prev) => ({ ...prev, password: "" }));
      await fetchCredentials();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save credentials.");
    } finally {
      setCredLoading(false);
    }
  };

  const testLogin = async () => {
    try {
      setCredLoading(true);
      setError(null);
      setSuccess(null);
      if (editingId && !credForm.password) {
        await nexaliaApi.testStoredCredentials(editingId);
      } else {
        await nexaliaApi.testCredentials(
          credForm.email.trim(),
          credForm.password,
        );
      }
      setSuccess("Login OK.");
    } catch (err) {
      setError(err.response?.data?.detail || "Login test failed.");
    } finally {
      setCredLoading(false);
    }
  };

  const startCron = async () => {
    try {
      setCronLoading(true);
      setError(null);
      setSuccess(null);
      await nexaliaApi.startCron(cronValue);
      setSuccess("Nexalia catalog cron started/updated.");
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Error starting the cron.");
    } finally {
      setCronLoading(false);
    }
  };

  const stopCron = async () => {
    try {
      setCronLoading(true);
      setError(null);
      setSuccess(null);
      await nexaliaApi.stopCron();
      setSuccess("Nexalia catalog cron stopped.");
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Error stopping the cron.");
    } finally {
      setCronLoading(false);
    }
  };

  const runScrape = async () => {
    try {
      setScanLoading(true);
      setError(null);
      setSuccess(null);
      const res = await nexaliaApi.scrapeCatalog({});
      const s = res.data || {};
      setSuccess(
        `Scrape done: ${s.products_upserted ?? s.products ?? "?"} products` +
          (s.categories
            ? ` across ${s.categories.length} categor(y/ies)`
            : "") +
          ".",
      );
      await fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || "Scrape failed.");
    } finally {
      setScanLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      setExportLoading(true);
      setError(null);
      const res = await nexaliaApi.exportProductsCsv(search, "name", "asc");
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `nexalia_products_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess("CSV download started.");
    } catch (err) {
      setError(err.response?.data?.detail || "CSV export failed.");
    } finally {
      setExportLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price == null || price === "") return "-";
    return String(price);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" mb={2}>
          Credentials
        </Typography>
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
        >
          <TextField
            label="Email"
            value={credForm.email}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, email: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label={editingId ? "Password (leave blank to keep)" : "Password"}
            type="password"
            value={credForm.password}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, password: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Categories (comma-separated slugs)"
            value={credForm.categories}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, categories: e.target.value }))
            }
            helperText="e.g. Beauty-Styling, Parfum-Lifestyle"
            fullWidth
            sx={{ gridColumn: { md: "1 / -1" } }}
          />
          <TextField
            label="In-stock qty"
            type="number"
            value={credForm.in_stock_qty}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, in_stock_qty: e.target.value }))
            }
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={saveCredentials}
            disabled={credLoading}
          >
            {editingId ? "Update credentials" : "Save credentials"}
          </Button>
          <Button variant="outlined" onClick={testLogin} disabled={credLoading}>
            Test login
          </Button>
          {credentials.length > 0 && (
            <Typography
              variant="body2"
              alignSelf="center"
              color="text.secondary"
            >
              Active: {credentials.find((c) => c.active)?.email || "—"}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" mb={2}>
          Catalog scrape & cron
        </Typography>

        <CronBuilderBox mb={2}>
          <Typography variant="subtitle1" mb={1}>
            Schedule
          </Typography>
          <Cron
            onChange={(e) => setCronValue(e)}
            value={cronValue}
            showResultText={true}
            showResultCron={true}
            locale="en"
          />
        </CronBuilderBox>

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={startCron}
            disabled={cronLoading}
          >
            Start / Update cron
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={stopCron}
            disabled={cronLoading}
          >
            Stop cron
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={runScrape}
            disabled={scanLoading}
          >
            {scanLoading ? "Scraping…" : "Run scrape now"}
          </Button>
          {(cronLoading || scanLoading) && <CircularProgress size={24} />}
        </Box>

        {cronStatus && (
          <Box sx={getMutedPanelSx(theme)}>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <Typography variant="body2">
              Status:{" "}
              {cronStatus.active ? (
                <span style={{ color: "green" }}>Active</span>
              ) : (
                <span style={{ color: "red" }}>Inactive</span>
              )}
            </Typography>
            <Typography variant="body2">
              Cron: {cronStatus.expression || cronStatus.schedule || "N/A"}
            </Typography>
            {(cronStatus.next_run_at || cronStatus.next_run) && (
              <Typography variant="body2">
                Next run:{" "}
                {cronStatus.active
                  ? new Date(
                      cronStatus.next_run_at || cronStatus.next_run,
                    ).toLocaleString()
                  : "-"}
              </Typography>
            )}
            {cronStatus.last_run_at && (
              <Typography variant="body2">
                Last run: {new Date(cronStatus.last_run_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6">Products ({totalItems})</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Search name / SKU / GTIN"
              variant="outlined"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            <Button
              onClick={fetchProducts}
              disabled={productsLoading}
              size="small"
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              onClick={runScrape}
              disabled={scanLoading}
              size="small"
              variant="contained"
              color="secondary"
            >
              {scanLoading ? "Importing…" : "Import Products"}
            </Button>
            <Button
              onClick={exportCsv}
              disabled={exportLoading}
              size="small"
              variant="contained"
              startIcon={
                exportLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ExportIcon />
                )
              }
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "name"}
                    direction={sortBy === "name" ? sortOrder : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>URL</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "sku"}
                    direction={sortBy === "sku" ? sortOrder : "asc"}
                    onClick={() => handleSort("sku")}
                  >
                    SKU
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "gtin13"}
                    direction={sortBy === "gtin13" ? sortOrder : "asc"}
                    onClick={() => handleSort("gtin13")}
                  >
                    GTIN
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "price"}
                    direction={sortBy === "price" ? sortOrder : "asc"}
                    onClick={() => handleSort("price")}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell>Currency</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No products yet. Save credentials and run a scrape.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>
                      {p.url ? (
                        <Link
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          Open
                        </Link>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{p.sku || "-"}</TableCell>
                    <TableCell>{p.gtin13 || "-"}</TableCell>
                    <TableCell>{formatPrice(p.price)}</TableCell>
                    <TableCell>{p.currency || "EUR"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default NexaliaTab;
